import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Users, ListChecks, ListTodo, StickyNote } from "lucide-react";
import { globalSearch } from "../lib/api";

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ clients: [], tasks: [], deadlines: [], observations: [], users: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) {
      setResults({ clients: [], tasks: [], deadlines: [], observations: [], users: [] });
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await globalSearch(q.trim());
        setResults(r);
      } catch {}
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  // Global shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Close on click outside
  useEffect(() => {
    const h = (e) => {
      if (open && boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, [open]);

  const go = (path) => {
    setOpen(false);
    setQ("");
    navigate(path);
  };

  const totalResults =
    results.clients.length + results.tasks.length + results.deadlines.length +
    results.observations.length + results.users.length;

  return (
    <>
      <button
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        data-testid="global-search-trigger"
        className="hidden md:flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 hover:bg-white dark:hover:bg-slate-700 transition-colors min-w-[220px]"
      >
        <Search className="h-4 w-4" />
        <span>Pesquisar...</span>
        <kbd className="ml-auto text-[10px] px-1.5 py-0.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded font-mono">⌘K</kbd>
      </button>
      <button
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        data-testid="global-search-trigger-mobile"
        className="md:hidden h-9 w-9 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
      >
        <Search className="h-4 w-4" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-24 px-4" data-testid="global-search-overlay">
          <div ref={boxRef} className="w-full max-w-2xl bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-700 px-4 py-3">
              <Search className="h-4 w-4 text-slate-400" />
              <input
                ref={inputRef}
                data-testid="global-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar clientes, tarefas, prazos, observações..."
                className="flex-1 bg-transparent outline-none text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400"
              />
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="max-h-[420px] overflow-y-auto p-2">
              {loading && <div className="p-6 text-center text-sm text-slate-400">Buscando...</div>}
              {!loading && q.trim().length < 2 && (
                <div className="p-6 text-center text-sm text-slate-400">Digite pelo menos 2 caracteres.</div>
              )}
              {!loading && q.trim().length >= 2 && totalResults === 0 && (
                <div className="p-6 text-center text-sm text-slate-400">Nenhum resultado.</div>
              )}
              {!loading && results.clients.length > 0 && (
                <ResultSection title="Clientes" icon={Users} testId="search-section-clients">
                  {results.clients.map((c) => (
                    <ResultRow key={c.id} onClick={() => go(`/clientes/${c.id}`)} testId={`search-result-client-${c.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{c.name}</p>
                        <p className="text-xs text-slate-500 truncate">
                          {c.document || "sem documento"}
                          {c.tags?.length ? ` • ${c.tags.join(", ")}` : ""}
                        </p>
                      </div>
                    </ResultRow>
                  ))}
                </ResultSection>
              )}
              {!loading && results.tasks.length > 0 && (
                <ResultSection title="Tarefas" icon={ListTodo} testId="search-section-tasks">
                  {results.tasks.map((t) => (
                    <ResultRow key={t.id} onClick={() => go("/tarefas")} testId={`search-result-task-${t.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{t.title}</p>
                        <p className="text-xs text-slate-500 truncate">
                          {t.status === "completed" ? "Concluída" : "Pendente"}
                          {t.due_date ? ` • ${t.due_date}` : ""}
                        </p>
                      </div>
                    </ResultRow>
                  ))}
                </ResultSection>
              )}
              {!loading && results.deadlines.length > 0 && (
                <ResultSection title="Prazos" icon={ListChecks} testId="search-section-deadlines">
                  {results.deadlines.map((d) => (
                    <ResultRow key={d.id} onClick={() => go("/prazos")} testId={`search-result-deadline-${d.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{d.tax_type}</p>
                        <p className="text-xs text-slate-500 truncate">Vence em {d.due_date}</p>
                      </div>
                    </ResultRow>
                  ))}
                </ResultSection>
              )}
              {!loading && results.observations.length > 0 && (
                <ResultSection title="Observações" icon={StickyNote} testId="search-section-observations">
                  {results.observations.map((o) => (
                    <ResultRow key={o.id} onClick={() => go(`/clientes/${o.client_id}`)} testId={`search-result-obs-${o.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{o.text}</p>
                        <p className="text-xs text-slate-500 truncate">por {o.author_name}</p>
                      </div>
                    </ResultRow>
                  ))}
                </ResultSection>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ResultSection({ title, icon: Icon, children, testId }) {
  return (
    <div data-testid={testId} className="mb-2">
      <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 px-3 py-1.5 flex items-center gap-1.5">
        <Icon className="h-3 w-3" />
        {title}
      </p>
      <div>{children}</div>
    </div>
  );
}

function ResultRow({ children, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
    >
      {children}
    </button>
  );
}
